<script lang="ts">
  import { navigate } from "svelte-routing";

  import {
    getRefererFromNavigator,
    getTokenFromNavigator,
    VerifyBodyResponse,
  } from "./types";
  import { confirm, verify } from "./api";
  import { email, kiwiServerEndpoint, lang } from "./stores";
  import { _ } from "svelte-i18n";
  import { Analytics, logEvent } from "firebase/analytics";
  import { PricingPlan } from "../functions/src/types";
  import ConfirmSteps from "./ConfirmSteps.svelte";

  export let analytics: Analytics;
  let showPopup = false;
  let password = "";
  let password2 = "";
  let ssoError: string | null = null;

  let registerForm: HTMLFormElement;
  let passwordPromise: Promise<void> | null = null;
  let ssoPromise: Promise<void> | null = null;
  let referer = getRefererFromNavigator();

  const verifyPromise: Promise<VerifyBodyResponse> = verify(getTokenFromNavigator())
    .then((res) => {
      if (!res.success) {
        throw new Error("TokenInvalid")
      }
      logEvent(analytics, "confirm_start", { name: "confirm_start" });
      return res.data as VerifyBodyResponse;
    });

  let registrationInfo: VerifyBodyResponse | null = null;
  verifyPromise.then((info) => { registrationInfo = info; }).catch(() => {});

  $: isPasswordLongEnough = password.length >= 15;
  $: isPasswordMatching = !!password && password === password2;
  $: isFormValid = isPasswordLongEnough && isPasswordMatching;

  function handlePassword(info: VerifyBodyResponse) {
    if (!registerForm.checkValidity()) return;

    const plainPassword = new FormData(registerForm).get("password")?.toString();
    if (!plainPassword) return;

    passwordPromise = confirmRegistration(info, plainPassword);
  }

  function handleSSO(info: VerifyBodyResponse) {
    if (ssoPromise) return;
    ssoError = null;
    ssoPromise = confirmRegistration(info, null)
      .catch((err) => { ssoError = (err as Error).message; })
      .finally(() => { ssoPromise = null; }) as Promise<void>;
  }

  async function confirmRegistration(info: VerifyBodyResponse, plainPassword: string | null): Promise<void> {
    try {
      const r = await confirm({...info, plainPassword}, getTokenFromNavigator())
      let errorText = $_("confirm.form.error.general");
      if (r.error || !r.success) {
        errorText = r.error?.data || r.error?.message ?? "";
        throw new Error(errorText)
      }

      email.set(info.email);
      lang.set(info.lang);
      kiwiServerEndpoint.set(info.kiwiServerEndpoint);
      logEvent(analytics, "confirm_password_sent", {
        name: "confirm_password_sent",
      });
      navigate("/login");
    } catch (err) {
      logEvent(analytics, "confirm_error", { ...err as Error, name: "confirm_error" });
      throw err;
    }
  }
</script>

<div class="wrapper thanks">
  {#await verifyPromise}
    <div class="verifing-token">
      <h1>{$_("confirm.token_verifying_header")}</h1>
      <img src="./assets/loader-6baa42.gif" alt="loader-6baa42.gif" />
    </div>
  {:then info}
    <div class="thanks-header">
      <h1>{$_("confirm.token_verified_header")}</h1>
    </div>

    <div class="form-wrapper">
      <form
        id="register-form"
        bind:this={registerForm}
        on:submit|preventDefault={() => handlePassword(info)}
      >
        <div class="form-column">
          <div class="input-wrapper">
            <label for="password">{$_("confirm.form.password")} *</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder=" "
              bind:value={password}
              required
            />
            {#if password.length > 0 && !isPasswordLongEnough}
              <span class="error-container" style:display="block">
                {$_("confirm.form.error.too_short_password")}
              </span>
            {/if}
          </div>

          <div class="form-column">
            <div>
              <label for="password2">{$_("confirm.form.password2")} *</label>
              <input
                type="password"
                id="password2"
                name="password2"
                placeholder=" "
                bind:value={password2}
                required
              />
              {#if password2.length > 0 && !isPasswordMatching}
                <span class="error-container" style:display="block">
                  {$_("confirm.form.error.diferent_passowrd")}
                </span>
              {/if}
            </div>

            <button
              class="submit-button"
              type="submit"
              disabled={!isFormValid || !!passwordPromise || !!ssoPromise}
            >
              {#await passwordPromise}
                <img src="./assets/loader.gif" alt="loader" />
              {:then}
                {$_("confirm.send")}
              {:catch}
                {$_("confirm.send")}
              {/await}
            </button>

            <label for="password2">{$_("confirm.or")}</label>

            <button
              class="submit-button-sso"
              on:click|preventDefault={() => (showPopup = true)}
            >
              <img src="./assets/microsoft.svg" alt="" />
              <img src="./assets/google.svg" alt="" />
              <span style="margin-left: 0.5rem;">{$_("confirm.use_sso")}</span>
            </button>
            {#await passwordPromise catch err}
              <span class="error-container" style:display="block"
                >{err.message}</span
              >
            {/await}
          </div>
        </div>
      </form>
    </div>
  {:catch}
    <div class="expired-token">
      <h1>{$_("confirm.token_expired_header")}</h1>
    </div>

    <div class="button-wrapper">
      <button
        class="register-button"
        on:click={() => {
          window.location.href = referer;
        }}>{$_("confirm.token_expired_redirect")}</button
      >
    </div>
  {/await}

  <ConfirmSteps></ConfirmSteps>
</div>

<div class="popup-wrapper" style:display={showPopup ? "flex" : "none"}>
  <div class="popup-content">
    <h2>{$_("confirm.use_sso")}</h2>
    <p style="text-align: left;">{$_("confirm.popup.sso_info")}</p>
    <button
      class="submit-button continue-button"
      on:click|preventDefault={() => registrationInfo && handleSSO(registrationInfo)}
    >
      {#if ssoPromise}
        <img src="./assets/loader-6baa42.gif" alt="loader-6baa42.gif" />
      {:else}
        {$_("confirm.use_sso")}
      {/if}
    </button>

    <button
      disabled={!!ssoPromise}
      class="submit-button back-button"
      on:click|preventDefault={() => {
        ssoError = null;
        showPopup = false;
      }}>Zpět</button
    >
    {#if ssoError}
      <div class="error-container" style:display="block">{ssoError}</div>
    {/if}
  </div>
</div>

<style lang="less" scoped>
  .popup-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: none;
    justify-content: center;
    align-items: center;
  }

  .popup-content {
    background-color: #fff;
    padding: 2rem;
    border-radius: 8px;
    text-align: center;
    width: 32rem;

    h2 {
      margin-bottom: 1rem;
    }

    p {
      margin-bottom: 2rem;
    }

    button {
      padding: 0.5rem 1rem;
      background-color: #6baa42;
      border: none;
      color: white;
      cursor: pointer;
    }

    .back-button {
      background-color: #313131;
      width: 100%;
      margin-top: 0.5rem;
    }

    .continue-button {
      color: #6baa42;
      border: 2px solid #6baa42;
      background-color: #fff;
      width: 100%;
    }
  }

  .wrapper {
    padding-top: 1.5rem;
  }

  .button-wrapper {
    margin-bottom: 5rem;
  }

  .register-button {
    font-size: 1.3rem;
    background-color: #6baa42;
    border: none;
    color: white;
    padding: 1.2rem 2.2rem;
    text-decoration: none;
    margin: 4px 2px;
    cursor: pointer;
    width: 22rem;
    text-transform: uppercase;
  }

  .input-wrapper {
    position: relative;
  }

  .error-container {
    display: none;
    text-align: left;
    margin-top: 0.5rem;

    font-size: 0.8rem;
    text-align: left;
    margin-top: 0.5rem;
    color: #fff;
    background-color: #ff5c4b;
    padding: 0.5rem;
  }

  .form-wrapper {
    width: 25rem;
    background-color: #f5f5f5;
    padding: 1.875rem 2.5rem;
    margin-bottom: 2rem;
  }

  #register-form {
    text-align: center;
  }

  .form-column {
    display: flex;
    flex-flow: column;
    width: 100%;

    label {
      font-size: 16px;
      font-weight: 500;
      text-align: left;
      margin-top: 13px;
    }

    div {
      flex: 1;
    }
  }

  input {
    width: 100%;
    padding: 12px 20px;
    margin-top: 13px;
    box-sizing: border-box;
  }

  input:invalid:not(:focus):not(:placeholder-shown) {
    outline: 2px solid #ad0101;
  }

  .submit-button {
    height: 3.25rem;

    img {
      height: 100%;
    }
  }

  .submit-button:disabled {
    background-color: #c0c0c0;
    color: #fff;
  }

  .submit-button,
  .submit-button-sso {
    display: flex;
    flex-flow: row;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    background-color: #6baa42;
    border: none;
    color: #fff;
    padding: 16px 32px;
    text-decoration: none;
    margin: 4px 2px;
    margin-top: 1rem;
    cursor: pointer;
    width: 100%;
    text-transform: uppercase;
  }

  .submit-button-sso {
    background-color: #fff;
    color: #6baa42;
    border: 2px solid #6baa42;
  }

  .submit-button-sso img,
  .submit-button-sso img {
    margin-right: 0.5rem;
    height: 1.2rem;
  }

  .thanks {
    display: flex;
    flex-flow: column;
    align-items: center;
  }

  .thanks-header,
  .expired-token,
  .verifing-token {
    white-space: pre-line;
    text-align: center;
    margin-bottom: 2rem;
    h1 {
      font-size: 1.5rem;
    }
  }

  @media only screen and (max-width: 600px) {
    .popup-content {
      width: 22rem;
      padding: 1rem 2rem;

      h2 {
        font-size: 1.3rem;
        margin-bottom: 1rem;
      }

      p {
        font-size: 0.9rem;
        margin-bottom: 2rem;
      }

      button {
        padding: 0.5rem 1rem;
        background-color: #6baa42;
        font-size: 0.8rem;
      }
    }

    .register-button {
      font-size: 0.8rem;
      padding: 1rem 2rem;
      margin: 4px 2px;
      width: 18rem;
    }

    .button-wrapper {
      margin-bottom: 2.5rem;
    }

    .wrapper {
      padding: 3rem 0.5rem;
    }

    .form-wrapper {
      width: 20rem;
      padding: 1.5rem 1.5rem;
      margin-bottom: 2.5rem;
    }

    .form-column {
      label {
        font-size: 0.8rem;
        margin-top: 1rem;
      }
    }

    .submit-button {
      height: 3.25rem;

      img {
        height: 100%;
      }
    }

    .submit-button,
    .submit-button-sso {
      font-size: 0.8rem;
      padding: 1rem 1rem;
      margin: 4px 2px;
      width: 100%;
    }

    .thanks-header,
    .expired-token,
    .verifing-token {
      margin-bottom: 1rem;
      h1 {
        font-size: 1.2rem;
        margin-bottom: 1rem;
      }
    }
  }
</style>
